import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Trophy, Medal, Clock, Target, BarChart3, Search } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export default function LeaderboardPage() {
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchTests();
  }, []);

  useEffect(() => {
    if (selectedTest) fetchLeaderboard(selectedTest);
  }, [selectedTest]);

  const fetchTests = async () => {
    try {
      const { data } = await api.get('/tests');
      setTests(data.data);
      if (data.data.length > 0) setSelectedTest(data.data[0]._id);
    } catch {
      toast.error('Failed to load tests');
    }
  };

  const fetchLeaderboard = async (testId) => {
    setLoading(true);
    try {
      const [lbRes, statsRes] = await Promise.all([
        api.get(`/results/leaderboard/${testId}?limit=100`),
        api.get(`/results/admin/summary?testId=${testId}`)
      ]);
      setLeaderboard(lbRes.data.data);
      setStats(statsRes.data.stats || {});
    } catch {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const filtered = leaderboard.filter(r =>
    !search || r.student?.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.student?.hallTicketNumber?.toLowerCase().includes(search.toLowerCase())
  );

  const rankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const scoreDistribution = leaderboard.reduce((acc, r) => {
    const bracket = Math.floor(r.totalScore / 20) * 20;
    const label = `${bracket}-${bracket + 19}`;
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  const chartData = Object.entries(scoreDistribution).map(([range, count]) => ({ range, count })).sort((a, b) => {
    const aStart = parseInt(a.range);
    const bStart = parseInt(b.range);
    return aStart - bStart;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Leaderboard & Analytics</h1>
        <p className="text-gray-500 mt-1">Student performance rankings</p>
      </div>

      {/* Test selector */}
      <div className="card">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Test</label>
        <select className="input-field max-w-md" value={selectedTest} onChange={e => setSelectedTest(e.target.value)}>
          <option value="">-- Select a test --</option>
          {tests.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
        </select>
      </div>

      {selectedTest && !loading && leaderboard.length > 0 && (
        <>
          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="card">
              <p className="text-sm text-gray-500">Total Submissions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAttempts || 0}</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Average Score</p>
              <p className="text-2xl font-bold text-primary-600">{stats.avgScore || 0}</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Average Accuracy</p>
              <p className="text-2xl font-bold text-green-600">{stats.avgAccuracy || 0}%</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Top Score</p>
              <p className="text-2xl font-bold text-amber-600">{stats.topScore || 0}</p>
            </div>
          </div>

          {/* Score Distribution Chart */}
          {chartData.length > 0 && (
            <div className="card">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <BarChart3 size={18} className="text-primary-600" /> Score Distribution
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Students">
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={`hsl(${220 + i * 15}, 70%, ${50 + i * 5}%)`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Leaderboard table */}
          <div className="card p-0 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Trophy size={18} className="text-amber-500" /> Rankings
              </h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input type="text" placeholder="Search student..." className="input-field pl-9 py-1.5 text-sm w-52"
                  value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-5 py-3 text-gray-500 font-semibold">Rank</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-semibold">Student</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-semibold">Score</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-semibold">Correct</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-semibold">Accuracy</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-semibold">Time</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-semibold">Percentile</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={r._id}
                      className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${r.rank <= 3 ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-5 py-3">
                        <span className={`font-bold text-lg ${r.rank === 1 ? 'text-amber-500' : r.rank === 2 ? 'text-gray-400' : r.rank === 3 ? 'text-amber-700' : 'text-gray-600'}`}>
                          {rankIcon(r.rank)}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {r.student?.photo ? (
                            <img src={r.student.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold text-xs">
                              {r.student?.name?.charAt(0)}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-800">{r.student?.name}</p>
                            <p className="text-xs text-gray-400 font-mono">{r.student?.hallTicketNumber}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="font-bold text-primary-700">{r.totalScore}</span>
                        <span className="text-gray-400 text-xs">/{r.maxScore}</span>
                      </td>
                      <td className="px-5 py-3 text-green-600 font-semibold">{r.correctAnswers}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-[60px]">
                            <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${r.accuracy}%` }}></div>
                          </div>
                          <span className={`font-medium text-xs ${r.accuracy >= 70 ? 'text-green-600' : r.accuracy >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                            {r.accuracy}%
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-600">
                        {Math.floor(r.timeTaken / 60)}m {r.timeTaken % 60}s
                      </td>
                      <td className="px-5 py-3">
                        <span className="bg-primary-100 text-primary-700 text-xs font-semibold px-2 py-1 rounded-full">
                          {r.percentile}%ile
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {selectedTest && !loading && leaderboard.length === 0 && (
        <div className="card text-center py-16 text-gray-400">
          <Trophy size={48} className="mx-auto mb-2 opacity-30" />
          <p>No submissions for this test yet</p>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-16"><div className="loader"></div></div>
      )}
    </div>
  );
}
